import React from 'react'

import { Chip } from '@mui/material'
import { alpha, lighten } from '@mui/material/styles'

import { useColorMode } from 'hooks'

// Direct path, not the `components` barrel: StatusChip is itself re-exported from that
// barrel, so importing through it would be a cycle (same reason `InfoBlock` does this).
import { InfoTooltip } from '../../feedback/InfoTooltip'

type Status = {
  label: string
  color: string
  borderColor?: string
  backgroundColor?: string
  /** Optional leading icon (inherits the badge's text color). */
  icon?: React.ReactElement
  /**
   * The `docs/glossary.json` entry that DEFINES this status. When set, hovering the CHIP
   * shows that definition (§44) — the chip is its own tooltip trigger, with no extra glyph.
   *
   * An id, not a sentence, for the reason §44 gives: the wording lives in one place and
   * every surface reads it. This mirrors `ScanStatusMeta.glossaryId` in `@repo/ui`'s
   * `scanStatus.ts`, which already carries the definition on the thing that DECIDES the
   * state rather than on each surface that draws it — same pattern, other status axis.
   *
   * ## Why the chip and not an `(i)` beside it
   *
   * A list renders one chip per row, so a glyph-per-chip would put 40 identical affordances
   * on a table to explain six words — which is how a reader learns to stop seeing them
   * (§59: "an `(i)` on everything is noise"). The chip already carries the word, so it is
   * the thing being asked about, and it costs no additional affordance.
   *
   * Opt-in per status: a status with no entry renders exactly as before. Most of the
   * `constants/statuses.ts` maps are self-evident labels ("In stock", "Archived") that owe
   * no definition, so this must not become a requirement on all of them.
   */
  glossaryId?: string
}

type Statuses = {
  [key: string]: Status
}

interface StatusChipProps {
  statuses: Statuses
  status: string
  size?: string
}

/**
 * A status's rendered fill / edge / text, per mode. THE single source for status colour.
 *
 * Exported because the Status FILTER has to paint the same chip: it previously received only the
 * status hue and re-derived a fill with `alpha(color, 0.14)`, which is the light-mode formula — so
 * in dark mode the filter showed pale near-white pills while the table beside it showed the dark
 * translucent tint. Measured: table Active `rgba(21,127,60,0.18)` against a filter chip painting the
 * authored light pastel. Sharing the function is the only way the two cannot drift.
 *
 * Light mode keeps the designed pastels. Dark mode derives a tinted look from the status hue —
 * translucent fill + tinted border + lightened text — so badges read on the dark surface instead of
 * appearing as pale near-white pills. (VERA-360/361)
 */
export const statusTone = (
  s: { color: string; borderColor?: string; backgroundColor?: string },
  isDark: boolean,
) =>
  isDark
    ? {
        color: lighten(s.color, 0.4),
        border: alpha(s.color, 0.4),
        background: alpha(s.color, 0.18),
      }
    : {
        color: s.color,
        border: s.borderColor ?? s.color,
        // Light mode: keep any designed pastel (s.backgroundColor); otherwise derive the same
        // translucent tint dark mode uses, so badges without an explicit background (e.g. scan
        // statuses) still read as filled, not outline-only.
        background: s.backgroundColor ?? alpha(s.color, 0.14),
      }

/**
 * Rendered height of a `size="small"` status token, in px.
 *
 * Exported because it is a FOOTPRINT that other things have to match, and every place that
 * re-derived it got it wrong. `SupportTypeBadge` and `AnnouncementCard`'s kind chip both sit
 * beside one of these and both had hand-written `minHeight: 28` copies; every loading skeleton
 * that stands in for one has to reserve exactly this many pixels or the row it is in renders
 * short and the whole page steps down when data lands (§19). Measured drift this closes: the
 * support request page drew its two header chips at 22 and the What's-new feed drew its kind
 * chip at 24, against a real 28 — 6px and 4px of layout shift respectively.
 *
 * Import this rather than typing 28 again.
 */
export const STATUS_TOKEN_H = 28

export const StatusChip = ({ statuses, status, size }: StatusChipProps) => {
  const { resolvedMode } = useColorMode()
  const isDark = resolvedMode === 'dark'

  if (status == undefined) {
    return null
  }

  if (!(status in statuses)) {
    // Handle the case where 'status' is not a valid key in 'statuses'.
    console.error(`Invalid 'status' prop: ${status}, statuses object do not contain this key.`)
    return null
  }

  const s = statuses[status]
  const isSmall = size == 'small'

  const tone = statusTone(s, isDark)

  const chip = (
    <Chip
      label={s.label}
      icon={s.icon}
      size={isSmall ? 'small' : 'medium'}
      sx={{
        // Hug the label — an explicit cross-size stops the chip stretching to full
        // width when it sits in a stretch flex column (e.g. an InfoItem "Status"
        // slot). No effect in table rows, where width is the main axis.
        width: 'fit-content',
        maxWidth: '100%',
        // Icon inherits the badge's text tone and sits snug before the label.
        '& .MuiChip-icon': { color: 'inherit', marginLeft: '8px', marginRight: '-2px' },
        // Non-small badges keep their existing tight look; the compact `small`
        // variant is a roomier rounded pill (used by the Address Book Default badge).
        ...(isSmall ? {} : { p: 0, pl: 0, pr: 0 }),
        // Status-column badges use the `small` variant; bump its height so the label
        // gets a touch more vertical padding (was 24px, read as cramped).
        height: isSmall ? STATUS_TOKEN_H : undefined,
        fontSize: isSmall ? 12 : 14,
        fontWeight: isSmall ? 500 : 400,
        borderRadius: isSmall ? '999px' : undefined,
        color: tone.color,
        border: `1px solid ${tone.border}`,
        backgroundColor: tone.background,
        ...(isSmall && {
          // Center the label + give the pill its horizontal breathing room.
          '& .MuiChip-label': {
            display: 'flex',
            alignItems: 'center',
            lineHeight: 1,
            paddingLeft: '12px',
            paddingRight: '12px',
          },
        }),
      }}/>
  )

  if (!s.glossaryId) return chip

  return (
    <InfoTooltip term={s.glossaryId}>
      {/*
        A span, not the Chip itself, for two reasons that both bite otherwise: MUI's Tooltip
        needs a child that holds a ref and forwards DOM props, and the chip's own
        `width: fit-content` has to be repeated here or the wrapper stretches in a flex column
        and the hover target grows to the width of whatever cell it sits in.

        IT MUST NOT READ AS NAVIGATING. `cursor: help`, never `pointer` — the chip is in a table
        row that DOES navigate on click, so a pointer cursor over it would promise a
        destination that is not there. No underline, no colour shift, no border change: the
        chip looks identical to an unexplained one, and the tooltip is the entire reveal. This
        is deliberate rather than an omission — a chip that visibly reacted to the pointer
        would invite the click, and the click belongs to the row.
      */}
      {/*
        DELIBERATELY NOT FOCUSABLE, which is the opposite of what `InfoTooltip`'s own glyph
        does — and the difference is CARDINALITY, not a lapse. That glyph appears once on a
        page; this appears once per ROW. Measured on /certificates: `tabIndex={0}` here put
        11 new tab stops in a grid that had 15, a 73% increase, to offer the same six
        definitions over and over — and it fights MUI DataGrid's roving-tabindex model,
        which gives cell content `params.tabIndex` (-1 on every unfocused cell) precisely so
        the grid is ONE stop rather than one per cell.

        What this costs: a keyboard-only user cannot raise the definition in place. It is a
        real cost and not a WCAG failure — the status WORD is in the DOM and read normally,
        and SC 1.4.13 governs how hover/focus content behaves, not that hover content must
        also be focusable. The definition stays reachable on the glossary page.

        The right home for a once-per-table, keyboard-reachable version is the STATUS COLUMN
        HEADER, which is one per table instead of one per row. Not built here.
      */}
      <span
        style={{ display: 'inline-flex', width: 'fit-content', cursor: 'help' }}
        onClick={(e) => {
          // COARSE POINTERS ONLY. `enterTouchDelay={0}` (see InfoTooltip) means a tap already
          // opens the tooltip — but the same tap then bubbles to the row and navigates away
          // from the page the reader was trying to understand, so on touch the affordance
          // would exist and be immediately undone. Swallowing the event fixes that, at the
          // cost of the status cell no longer opening the row on a touch device; the rest of
          // the row still does.
          //
          // Gated on `hover: none` so a MOUSE click is untouched: clicking a status cell with
          // a mouse opens the row exactly as it does today, because a mouse already had the
          // definition on hover and never needed the click.
          if (window.matchMedia?.('(hover: none)').matches) e.stopPropagation()
        }}
      >
        {chip}
      </span>
    </InfoTooltip>
  )
}
